"use client";
import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";

export function HoverBorderGradient({
  children,
  containerClassName,
  className,
  as: Tag = "button",
  duration = 1,
  clockwise = true,
  ...props
}) {
  const [hovered, setHovered] = useState(false);
  const [direction, setDirection] = useState("TOP");

  const rotateDirection = (currentDirection) => {
    const directions = ["TOP", "LEFT", "BOTTOM", "RIGHT"];
    const currentIndex = directions.indexOf(currentDirection);
    const nextIndex = clockwise
      ? (currentIndex - 1 + directions.length) % directions.length
      : (currentIndex + 1) % directions.length;
    return directions[nextIndex];
  };

  // Silver line positions
  const movingMap = {
    TOP: "radial-gradient(20.7% 50% at 50% 0%, #C0C0C0 0%, rgba(192, 192, 192, 0) 100%)",
    LEFT: "radial-gradient(16.6% 43.1% at 0% 50%, #C0C0C0 0%, rgba(192, 192, 192, 0) 100%)",
    BOTTOM:
      "radial-gradient(20.7% 50% at 50% 100%, #C0C0C0 0%, rgba(192, 192, 192, 0) 100%)",
    RIGHT:
      "radial-gradient(16.2% 41.199999999999996% at 100% 50%, #C0C0C0 0%, rgba(192, 192, 192, 0) 100%)",
  };

  // Highlight color on hover
  const highlight = "linear-gradient(135deg, #0E7FA8 0%, #3640C8 100%)";

  useEffect(() => {
    if (!hovered) {
      const interval = setInterval(() => {
        setDirection((prevState) => rotateDirection(prevState));
      }, duration * 1000);
      return () => clearInterval(interval);
    }
  }, [hovered]);

  return (
    <Tag
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        "relative flex rounded-xl border border-zinc-600 bg-black/30 hover:bg-black/20 transition duration-500 items-center justify-center overflow-visible p-px w-fit shadow-[0_0_6px_rgba(180,180,180,0.15)] hover:scale-[1.02] active:scale-[0.98] transition-transform",
        containerClassName
      )}
      {...props}
    >
      <div
        className={cn(
          "w-auto z-10 bg-black px-6 py-2.5 rounded-[inherit] text-white font-medium",
          className
        )}
      >
        {children}
      </div>
      <motion.div
        className="flex-none inset-0 overflow-hidden absolute z-0 rounded-[inherit]"
        style={{
          filter: "blur(1.5px)",
          position: "absolute",
          width: "100%",
          height: "100%",
        }}
        initial={{ background: movingMap[direction] }}
        animate={{
          background: hovered
            ? [movingMap[direction], highlight]
            : movingMap[direction],
        }}
        transition={{ ease: "linear", duration: duration ?? 1 }}
      />
      <div className="bg-black absolute z-1 flex-none inset-[2px] rounded-[inherit]" />
    </Tag>
  );
}
